import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CommunityStandards from "@/pages/CommunityStandards";
import Welcome from "@/pages/Welcome";
import LegalDoc from "@/pages/LegalDoc";
import Faq from "@/pages/Faq";

import Onboarding from "@/pages/member/Onboarding";
import MemberLayout from "@/pages/member/MemberLayout";
import Home from "@/pages/member/Home";
import Discover from "@/pages/member/Discover";
import MyOutings from "@/pages/member/MyOutings";
import OutingDetail from "@/pages/member/OutingDetail";
import Messages from "@/pages/member/Messages";
import Profile from "@/pages/member/Profile";
import Resources from "@/pages/member/Resources";
import Support from "@/pages/member/Support";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminMembers from "@/pages/admin/Members";
import AdminGroups from "@/pages/admin/Groups";
import AdminOutings from "@/pages/admin/Outings";
import AdminVenueApprovals from "@/pages/admin/VenueApprovals";
import AdminMutual from "@/pages/admin/MutualInterest";
import AdminReports from "@/pages/admin/Reports";
import AdminSupportTickets from "@/pages/admin/SupportTickets";
import AdminActivities from "@/pages/admin/Activities";
import AdminVenues from "@/pages/admin/Venues";
import AdminMarkets from "@/pages/admin/Markets";
import AdminCodes from "@/pages/admin/Codes";
import AdminSafetyReview from "@/pages/admin/SafetyReview";
import AdminSettings from "@/pages/admin/Settings";

function FullLoader() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="h-10 w-10 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
        </div>
    );
}

function RequireMember({ children }) {
    const { user, checked } = useAuth();
    const location = useLocation();
    if (!checked || user === null) return <FullLoader />;
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (!user.onboarding_complete && location.pathname !== "/onboarding")
        return <Navigate to="/onboarding" replace />;
    return children;
}

function RequireAdmin({ children }) {
    const { user, checked } = useAuth();
    if (!checked || user === null) return <FullLoader />;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") return <Navigate to="/app" replace />;
    return children;
}

function PublicOnly({ children }) {
    const { user, checked } = useAuth();
    if (!checked || user === null) return <FullLoader />;
    if (user && user.role === "admin") return <Navigate to="/admin" replace />;
    if (user) return <Navigate to="/app" replace />;
    return children;
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/community-standards" element={<CommunityStandards />} />
                    <Route path="/faq" element={<Faq />} />
                    <Route path="/terms" element={<LegalDoc slug="terms" />} />
                    <Route path="/privacy" element={<LegalDoc slug="privacy" />} />
                    <Route path="/community-rules" element={<LegalDoc slug="community-rules" />} />
                    <Route path="/safety" element={<LegalDoc slug="safety" />} />
                    <Route path="/welcome" element={<Welcome />} />
                    <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
                    <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    <Route path="/onboarding" element={<RequireMember><Onboarding /></RequireMember>} />
                    <Route element={<RequireMember><MemberLayout /></RequireMember>}>
                        <Route path="/app" element={<Home />} />
                        <Route path="/discover" element={<Discover />} />
                        <Route path="/outings" element={<MyOutings />} />
                        <Route path="/outings/:id" element={<OutingDetail />} />
                        <Route path="/messages" element={<Messages />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/resources" element={<Resources />} />
                        <Route path="/support" element={<Support />} />
                    </Route>

                    <Route element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/analytics" element={<AdminAnalytics />} />
                        <Route path="/admin/members" element={<AdminMembers />} />
                        <Route path="/admin/groups" element={<AdminGroups />} />
                        <Route path="/admin/outings" element={<AdminOutings />} />
                        <Route path="/admin/venue-approvals" element={<AdminVenueApprovals />} />
                        <Route path="/admin/mutual-interest" element={<AdminMutual />} />
                        <Route path="/admin/reports" element={<AdminReports />} />
                        <Route path="/admin/safety-review" element={<AdminSafetyReview />} />
                        <Route path="/admin/support-tickets" element={<AdminSupportTickets />} />
                        <Route path="/admin/activities" element={<AdminActivities />} />
                        <Route path="/admin/venues" element={<AdminVenues />} />
                        <Route path="/admin/markets" element={<AdminMarkets />} />
                        <Route path="/admin/codes" element={<AdminCodes />} />
                        <Route path="/admin/settings" element={<AdminSettings />} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <Toaster position="top-center" richColors />
            </BrowserRouter>
        </AuthProvider>
    );
}
